import React from "react";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileUpload, type UploadFileItem } from "@/client/components/FileUpload";
import { ImageSizeSelector } from "@/client/components/ImageSizeSelector";
import { ImagePlus } from "lucide-react";
import { FieldLabel } from "@/features/workspace/schema/SchemaFormRenderer";
import { getSemanticIcon } from "@/features/workspace/schema/renderers/FieldIconHelper";
import { resolveUploadMaxFiles } from "@/features/workspace/schema/helpers";
import type {
  SchemaDataFieldDefinition,
  SchemaFieldOption,
  SchemaFormDefinition,
  SchemaFormValues,
  SchemaSelectField,
  SchemaRadioField,
  SchemaToggleField,
} from "@/features/workspace/schema/types";
import { SelectFieldInput } from "@/features/workspace/schema/renderers/SelectInputRenderer";
import { RadioFieldInput } from "@/features/workspace/schema/renderers/RadioInputRenderer";
import { ToggleFieldInput } from "@/features/workspace/schema/renderers/ToggleInputRenderer";

interface FieldInputProps<FieldKey extends string> {
  field: SchemaDataFieldDefinition<FieldKey>;
  schema: SchemaFormDefinition<FieldKey>;
  values: SchemaFormValues<FieldKey>;
  modelKey: string;
  disabled: boolean;
  errorMessage: string | null;
  touched: boolean;
  translate: (key?: string, arg2?: string | Record<string, unknown>) => string;
  optionsCache: React.MutableRefObject<Map<string, SchemaFieldOption[]>>;
  onChange: (value: unknown) => void;
  onBlur: () => void;
  onPatchValues?: (patch: Partial<SchemaFormValues<FieldKey>>) => void;
  useIconLabels?: boolean;
  /** 紧凑模式 - 用于 Dock 场景 */
  compact?: boolean;
}

type _SemanticIconType = ReturnType<typeof getSemanticIcon>;

const FieldInput = <FieldKey extends string,>({
  field,
  schema,
  values,
  modelKey,
  disabled,
  errorMessage,
  touched,
  translate,
  optionsCache,
  onChange,
  onBlur,
  onPatchValues,
  useIconLabels,
  compact = false,
}: FieldInputProps<FieldKey>) => {
  const value = values[field.key];
  const label = translate(field.labelKey);
  const placeholder = translate(field.placeholderKey);
  const helper = translate(field.helperTextKey, field.helperTextParams);
  const displayErrorKey = touched ? errorMessage : null;
  const displayErrorText = displayErrorKey ? translate(displayErrorKey, displayErrorKey) : null;
  const helperText = displayErrorText ?? helper;

  if (field.type === "text" || field.type === "textarea" || field.type === "number") {
    const numeric = field.type === "number";
    const inputValue =
      numeric && typeof value === "number"
        ? value
        : typeof value === "string"
          ? value
          : numeric
            ? ""
            : "";
    const isPrimaryPrompt = (schema.prompt?.primaryField as FieldKey | undefined) === field.key;
    const suppressPromptRequiredText =
      isPrimaryPrompt && displayErrorKey === "pages:modelWorkspace.form.validation.required";
    const effectiveErrorText = suppressPromptRequiredText ? null : displayErrorText;
    const effectiveHelperText = effectiveErrorText ?? helper;
    return (
      <div className={useIconLabels ? "flex items-center gap-2" : "flex flex-col gap-1"}>
        <FieldLabel label={label} useIconLabels={useIconLabels} fieldKey={String(field.key)} fieldType={field.type} />
        {field.type === "textarea" ? (
          <Textarea
            className="focus-visible:border-input focus-visible:ring-0 focus-visible:ring-transparent"
            placeholder={placeholder}
            value={typeof inputValue === "string" ? inputValue : String(inputValue ?? "")}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            disabled={disabled}
            rows={field.minRows ?? 4}
          />
        ) : (
          <Input
            className="focus-visible:border-input focus-visible:ring-0 focus-visible:ring-transparent"
            type={numeric ? "number" : "text"}
            placeholder={placeholder}
            value={typeof inputValue === "string" ? inputValue : String(inputValue ?? "")}
            onChange={(e) => {
              if (numeric) {
                const next = e.target.value;
                onChange(next === "" ? "" : Number(next));
              } else {
                onChange(e.target.value);
              }
            }}
            onBlur={onBlur}
            disabled={disabled}
          />
        )}
        {effectiveErrorText ? (
          <p className="text-xs text-destructive">{effectiveErrorText}</p>
        ) : effectiveHelperText ? (
          <p className="text-xs text-muted-foreground">{effectiveHelperText}</p>
        ) : null}
        {numeric && field.unitKey ? (
          <p className="text-[11px] text-muted-foreground">{translate(field.unitKey)}</p>
        ) : null}
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <SelectFieldInput
        field={field as SchemaSelectField<FieldKey>}
        schema={schema}
        values={values}
        modelKey={modelKey}
        disabled={disabled}
        label={label}
        helperText={helperText}
        errorText={displayErrorText}
        placeholder={placeholder}
        translate={translate}
        optionsCache={optionsCache}
        onChange={onChange}
        onBlur={onBlur}
        useIconLabels={useIconLabels}
      />
    );
  }

  if (field.type === "radio") {
    return (
      <RadioFieldInput
        field={field as SchemaRadioField<FieldKey>}
        schema={schema}
        values={values}
        modelKey={modelKey}
        disabled={disabled}
        label={label}
        helperText={helperText}
        errorText={displayErrorText}
        translate={translate}
        optionsCache={optionsCache}
        onChange={onChange}
        onBlur={onBlur}
        useIconLabels={useIconLabels}
      />
    );
  }

  if (field.type === "toggle") {
    return (
      <ToggleFieldInput
        field={field as SchemaToggleField<FieldKey>}
        schema={schema}
        values={values}
        modelKey={modelKey}
        disabled={disabled}
        label={label}
        helperText={helperText}
        errorText={displayErrorText}
        translate={translate}
        optionsCache={optionsCache}
        onChange={onChange}
        onBlur={onBlur}
        useIconLabels={useIconLabels}
      />
    );
  }

  if (field.type === "switch") {
    const checked = typeof value === "boolean" ? value : Boolean(field.defaultValue);
    return (
      <div className="flex items-center gap-2">
        <FieldLabel label={label} useIconLabels={useIconLabels} fieldKey={String(field.key)} fieldType={field.type} />
        <Switch checked={checked} onCheckedChange={(next) => onChange(next)} disabled={disabled} />
        {helperText ? (
          <span className="flex-1 text-right text-xs text-muted-foreground">
            {helperText}
          </span>
        ) : null}
      </div>
    );
  }

  if (field.type === "tags") {
    const list = Array.isArray(value) ? (value as string[]) : [];
    const display = list.join(", ");
    return (
      <div className={useIconLabels ? "flex items-center gap-2" : "flex flex-col gap-1"}>
        <FieldLabel label={label} useIconLabels={useIconLabels} fieldKey={String(field.key)} fieldType={field.type} />
        <Input
          className="focus-visible:border-input focus-visible:ring-0 focus-visible:ring-transparent"
          placeholder={placeholder}
          value={display}
          onChange={(e) => {
            const raw = e.target.value as string;
            const next = raw
              .split(/[\,\n]/)
              .map((s) => s.trim())
              .filter(Boolean);
            onChange(next);
          }}
          onBlur={onBlur}
          disabled={disabled}
        />
        {displayErrorText ? (
          <p className="text-xs text-destructive">{displayErrorText}</p>
        ) : helperText ? (
          <p className="text-xs text-muted-foreground">{helperText}</p>
        ) : null}
      </div>
    );
  }

  if (field.type === "slider") {
    const sliderValue = typeof value === "number" ? value : field.min;
    return (
      <div className={useIconLabels ? "flex items-center gap-2" : "flex flex-col gap-1"}>
        <FieldLabel label={label} useIconLabels={useIconLabels} fieldKey={String(field.key)} fieldType={field.type} />
        <Slider
          value={[sliderValue]}
          min={field.min}
          max={field.max}
          step={field.step}
          onValueChange={(vals) => onChange(vals[0] ?? sliderValue)}
          onPointerUp={onBlur as any}
          disabled={disabled}
        />
        {displayErrorText ? (
          <p className="text-xs text-destructive">{displayErrorText}</p>
        ) : null}
      </div>
    );
  }

  if (field.type === "imageSize") {
    const currentValue =
      typeof value === "string"
        ? value
        : typeof field.defaultValue === "string"
          ? (field.defaultValue as string)
          : "";
    const resolvedSizeOptions = field.sizeOptions?.map((option) => ({
      label: option.labelKey ? translate(option.labelKey) : option.label ?? `${option.value}px`,
      value: option.value,
    }));
    const aspectFieldKey = field.aspectField;
    const aspectValue =
      aspectFieldKey && typeof values[aspectFieldKey] === "string"
        ? (values[aspectFieldKey] as string)
        : undefined;
    const resolvedAspectOptions = field.aspectOptions?.map((option, index) => ({
      id: option.id ?? option.value ?? `aspect-${index}`,
      value: option.value,
      ratio: option.ratio,
      label: option.labelKey ? translate(option.labelKey) : option.label ?? "",
      description: option.descriptionKey ? translate(option.descriptionKey) : option.description,
      resolution: option.resolution,
    }));
    return (
      <div className={useIconLabels ? "flex items-center gap-2" : "flex flex-col gap-1"}>
        {useIconLabels && (
          <FieldLabel label={label} useIconLabels={useIconLabels} fieldKey={String(field.key)} fieldType={field.type} />
        )}
        <ImageSizeSelector
          value={currentValue}
          label={useIconLabels ? undefined : (compact ? undefined : label)}
          variant={(useIconLabels || compact) ? "compact" : "default"}
          options={resolvedSizeOptions}
          aspectValue={aspectValue}
          aspectOptions={resolvedAspectOptions}
          disabled={disabled}
          onChange={(next) => onChange(next)}
          onAspectValueChange={
            aspectFieldKey && onPatchValues
              ? (nextAspect, nextResolution) => {
                  const patch: Partial<SchemaFormValues<FieldKey>> = {};
                  if (nextAspect !== undefined) {
                    patch[aspectFieldKey] = nextAspect;
                  }
                  if (nextResolution && nextResolution !== values[field.key]) {
                    patch[field.key] = nextResolution;
                  }
                  if (Object.keys(patch).length > 0) {
                    onPatchValues(patch);
                  }
                }
              : undefined
          }
        />
        {displayErrorText ? (
          <p className="text-xs text-destructive">{displayErrorText}</p>
        ) : helperText ? (
          <p className="text-xs text-muted-foreground">{helperText}</p>
        ) : null}
      </div>
    );
  }

  if (field.type === "file" || field.type === "imageUpload") {
    const items = Array.isArray(value) ? (value as UploadFileItem[]) : [];
    const maxFiles = resolveUploadMaxFiles(field as SchemaDataFieldDefinition<FieldKey>, values, modelKey);
    const accept = field.type === "imageUpload" ? (field as any).accept ?? "image/*" : (field as any).accept;
    const compact = Boolean((field as any).metadata?.compactUpload);
    const maxSizeMB = (field as any).maxSizeMB as number | undefined;
    const maxDimension = (field as any).maxDimension as number | undefined;
    const imageResizeMode = (field as any).imageResizeMode as any;
    const imageConstraints = (field as any).imageConstraints as any;

    if (compact) {
      const [open, setOpen] = React.useState(false);
      const selectedCount = items.length;
      return (
        <div className="relative">
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <button
                  type="button"
                  aria-label={label}
                  aria-disabled={disabled ? true : undefined}
                  onClick={() => {
                    if (!disabled) setOpen(true);
                  }}
                  className="h-[72px] w-[72px] rounded-xl border border-primary/20 bg-primary/5 flex flex-col items-center justify-center gap-1 text-foreground/70 outline-none transition hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={disabled}
                >
                  <ImagePlus className="size-5" />
                  <span className="text-[10px] leading-none text-foreground/80 max-w-[56px] text-center truncate">
                    {label}
                  </span>
                </button>
                {selectedCount > 0 ? (
                  <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] leading-none rounded-full h-5 min-w-5 px-1 flex items-center justify-center">
                    {selectedCount}
                  </span>
                ) : null}
              </span>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
          </Tooltip>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="sm:max-w-lg">
              <DialogTitle>{label}</DialogTitle>
              <FileUpload
                value={items}
                onFilesChange={(next) => onChange(next)}
                accept={accept}
                maxSizeMB={maxSizeMB}
                maxDimension={maxDimension}
                imageResizeMode={imageResizeMode}
                imageConstraints={imageConstraints}
                maxFiles={maxFiles}
                multiple={maxFiles > 1}
              />
              {displayErrorText ? (
                <p className="text-xs text-destructive">{displayErrorText}</p>
              ) : helperText ? (
                <p className="text-xs text-muted-foreground">{helperText}</p>
              ) : null}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  {translate("common:actions.close", "关闭")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      );
    }

    return (
      <div className={useIconLabels ? "flex items-center gap-2" : "flex flex-col gap-1"}>
        <FieldLabel label={label} useIconLabels={useIconLabels} iconVariant="file" className="mb-1" fieldKey={String(field.key)} fieldType={field.type} />
        <FileUpload
          value={items}
          onFilesChange={(next) => onChange(next)}
          accept={accept}
          maxSizeMB={maxSizeMB}
          maxDimension={maxDimension}
          imageResizeMode={imageResizeMode}
          imageConstraints={imageConstraints}
          maxFiles={maxFiles}
          multiple={maxFiles > 1}
        />
        {displayErrorText ? (
          <p className="text-xs text-destructive">{displayErrorText}</p>
        ) : helperText ? (
          <p className="text-xs text-muted-foreground">{helperText}</p>
        ) : null}
      </div>
    );
  }

  return null;
};

export default FieldInput;
